import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Stack,
  Badge,
  useTheme
} from '@mui/material';
import { ledeSx } from '../theme/styleHelpers';
import { StatCard } from '../components/common/StatCard';
import {
  ArrowBack,
  NotificationsActive,
  Refresh,
  Email,
  CheckCircle,
  Cancel,
  Schedule,
  Block,
  HourglassEmpty
} from '@mui/icons-material';
import { useOrganization } from '../context/OrganizationContext';
import PersonalInvitations from '../components/user/PersonalInvitations';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`invitations-tabpanel-${index}`}
      aria-labelledby={`invitations-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Invitations: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  const {
    invitations,
    refreshInvitations,
    isLoading,
    error
  } = useOrganization();

  // Filter invitations by status
  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
  const acceptedInvitations = invitations.filter(inv => inv.status === 'accepted');
  const declinedInvitations = invitations.filter(inv => inv.status === 'declined');
  const canceledInvitations = invitations.filter(inv => inv.status === 'canceled');
  const expiredInvitations = invitations.filter(inv => inv.status === 'expired');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = async () => {
    try {
      await refreshInvitations();
    } catch (error) {
      console.error('Failed to refresh invitations:', error);
    }
  };

  useEffect(() => {
    // Refresh invitations when component mounts
    handleRefresh();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/dashboard')}
            sx={{ 
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' }
            }}
          >
            <ArrowBack />
          </IconButton>
          
          <Box>
            <Typography
              variant="h1"
              sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: 1 }}
            >
              All Invitations
            </Typography>
            <Typography sx={ledeSx}>
              Manage all your organization and team invitations.
            </Typography>
          </Box>
        </Box>

        <Tooltip title="Refresh invitations">
          <IconButton 
            onClick={handleRefresh}
            disabled={isLoading}
            sx={{ 
              bgcolor: 'background.paper',
              boxShadow: 1 
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box
        sx={{
          mb: 4,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 1.5,
        }}
      >
        <StatCard label="Pending"  value={pendingInvitations.length}  icon={<Schedule />}        />
        <StatCard label="Accepted" value={acceptedInvitations.length} icon={<CheckCircle />}     />
        <StatCard label="Declined" value={declinedInvitations.length} icon={<Cancel />}          />
        <StatCard label="Canceled" value={canceledInvitations.length} icon={<Block />}           />
        <StatCard label="Expired"  value={expiredInvitations.length}  icon={<HourglassEmpty />}  />
        <StatCard label="Total"    value={invitations.length}         icon={<Email />}           />
      </Box>

      {/* Main Content */}
      <Card
        sx={{
          borderRadius: 2,
          borderColor: 'divider',
          boxShadow: 'none',
          '&:hover': { transform: 'none', boxShadow: 'none' }
        }}
      >
        <CardHeader
          avatar={<NotificationsActive color="primary" />}
          title="Invitation Management"
          subheader="View and manage all your invitations by status"
        />
        <Divider />
        <CardContent>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="invitation status tabs">
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule />
                    Pending
                    {pendingInvitations.length > 0 && (
                      <Chip 
                        label={pendingInvitations.length} 
                        size="small" 
                        color="warning"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                id="invitations-tab-0"
                aria-controls="invitations-tabpanel-0"
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    Accepted
                    {acceptedInvitations.length > 0 && (
                      <Chip 
                        label={acceptedInvitations.length} 
                        size="small" 
                        color="success"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                id="invitations-tab-1"
                aria-controls="invitations-tabpanel-1"
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel />
                    Declined
                    {declinedInvitations.length > 0 && (
                      <Chip 
                        label={declinedInvitations.length} 
                        size="small" 
                        color="error"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                id="invitations-tab-2"
                aria-controls="invitations-tabpanel-2"
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Block />
                    Canceled
                    {canceledInvitations.length > 0 && (
                      <Chip
                        label={canceledInvitations.length}
                        size="small"
                        color="default"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                id="invitations-tab-3"
                aria-controls="invitations-tabpanel-3"
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HourglassEmpty />
                    Expired
                    {expiredInvitations.length > 0 && (
                      <Chip
                        label={expiredInvitations.length}
                        size="small"
                        color="default"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                id="invitations-tab-4"
                aria-controls="invitations-tabpanel-4"
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email />
                    All
                    {invitations.length > 0 && (
                      <Chip
                        label={invitations.length}
                        size="small"
                        color="info"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                id="invitations-tab-5"
                aria-controls="invitations-tabpanel-5"
              />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            {pendingInvitations.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You have {pendingInvitations.length} pending invitation{pendingInvitations.length !== 1 ? 's' : ''} waiting for your response.
                </Typography>
                <PersonalInvitations statusFilter="pending" />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Schedule sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Pending Invitations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You're all caught up! No invitations are waiting for your response.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {acceptedInvitations.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You have accepted {acceptedInvitations.length} invitation{acceptedInvitations.length !== 1 ? 's' : ''}.
                </Typography>
                <PersonalInvitations statusFilter="accepted" />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CheckCircle sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Accepted Invitations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Invitations you accept will appear here.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {declinedInvitations.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You have declined {declinedInvitations.length} invitation{declinedInvitations.length !== 1 ? 's' : ''}.
                </Typography>
                <PersonalInvitations statusFilter="declined" />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Cancel sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Declined Invitations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Invitations you decline will appear here.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {canceledInvitations.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {canceledInvitations.length} canceled invitation{canceledInvitations.length !== 1 ? 's' : ''}.
                </Typography>
                <PersonalInvitations statusFilter="canceled" />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Block sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Canceled Invitations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Canceled invitations will appear here.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {expiredInvitations.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {expiredInvitations.length} expired invitation{expiredInvitations.length !== 1 ? 's' : ''}.
                </Typography>
                <PersonalInvitations statusFilter="expired" />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <HourglassEmpty sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Expired Invitations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expired invitations will appear here.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            {invitations.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  All your invitations ({invitations.length} total) across all organizations and teams.
                </Typography>
                <PersonalInvitations statusFilter="all" />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Email sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Invitations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  You haven't received any invitations yet. When someone invites you to join their organization or team, invitations will appear here.
                </Typography>
                <Button
                  component={Link}
                  to="/dashboard"
                  variant="contained"
                  startIcon={<ArrowBack />}
                >
                  Back to Dashboard
                </Button>
              </Box>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Invitations;